import { useState } from "react";
import { Navigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";
import { routes } from "@/constants/routes";

type Slide = { src: string; titleKey: string; descKey: string };

const SLIDES: Slide[] = [
  {
    src: "/preview/home.PNG",
    titleKey: "preview.slides.home.title",
    descKey: "preview.slides.home.desc",
  },
  {
    src: "/preview/upload.PNG",
    titleKey: "preview.slides.upload.title",
    descKey: "preview.slides.upload.desc",
  },
  {
    src: "/preview/keys.PNG",
    titleKey: "preview.slides.keys.title",
    descKey: "preview.slides.keys.desc",
  },
  {
    src: "/preview/import.PNG",
    titleKey: "preview.slides.import.title",
    descKey: "preview.slides.import.desc",
  },
  {
    src: "/preview/quarantine.PNG",
    titleKey: "preview.slides.quarantine.title",
    descKey: "preview.slides.quarantine.desc",
  },
  {
    src: "/preview/trash.PNG",
    titleKey: "preview.slides.trash.title",
    descKey: "preview.slides.trash.desc",
  },
  {
    src: "/preview/help.PNG",
    titleKey: "preview.slides.help.title",
    descKey: "preview.slides.help.desc",
  },
  {
    src: "/preview/dark.PNG",
    titleKey: "preview.slides.dark.title",
    descKey: "preview.slides.dark.desc",
  },
];

function PreviewImage({ src, alt }: { src: string; alt: string }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <Box
        sx={{
          minHeight: { xs: 160, sm: 200 },
          bgcolor: "action.hover",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: 2,
        }}
      >
        <ImageOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textAlign: "center",
          }}
        >
          {t("preview.imageUnavailable")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{
        width: "100%",
        maxWidth: "100%",
        height: "auto",
        display: "block",
        verticalAlign: "bottom",
        maxHeight: {
          xs: "min(55vh, 360px)",
          sm: "min(60vh, 480px)",
          md: "none",
        },
        objectFit: "contain",
      }}
    />
  );
}

export default function PreviewPage() {
  const { t } = useTranslation();
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={routes.dashboard} replace />;
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 3, md: 6 },
        px: { xs: 2, sm: 3 },
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          mb: 4,
          textAlign: { xs: "left", sm: "center" },
          display: "flex",
          flexDirection: "column",
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Typography
          variant="overline"
          color="primary"
          sx={{ letterSpacing: 0.12, fontWeight: 700 }}
        >
          {t("app.name")}
        </Typography>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 800,
            mt: 0.5,
            mb: 1.5,
            fontSize: { xs: "1.75rem", md: "2.5rem" },
          }}
        >
          {t("preview.title")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            maxWidth: 640,
            mx: { sm: "auto" },
            lineHeight: 1.7,
            wordBreak: "break-word",
          }}
        >
          {t("preview.subtitle")}
        </Typography>
        <Button
          component={RouterLink}
          to={routes.home}
          variant="outlined"
          sx={{
            mt: 3,
            width: { xs: "100%", sm: "auto" },
            alignSelf: { sm: "center" },
          }}
        >
          {t("preview.backHome")}
        </Button>
      </Box>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {SLIDES.map((slide) => (
          <Grid key={slide.src} size={{ xs: 12, sm: 6 }}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  bgcolor: "grey.100",
                  borderBottom: 1,
                  borderColor: "divider",
                  lineHeight: 0,
                  display: "flex",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <PreviewImage src={slide.src} alt={t(slide.titleKey)} />
              </Box>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {t(slide.titleKey)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.65,
                    wordBreak: "break-word",
                  }}
                >
                  {t(slide.descKey)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
